import axios from "axios";

const development = !process.env.NODE_ENV || process.env.NODE_ENV === 'development';
const backendUrl = development ? 'http://127.0.0.1:8000/' : 'https://api.yeticlean.co.uk/';

// --- Axios instances and interceptors (Unchanged) ---
export const AxiosPublic = axios.create({
    baseURL: backendUrl,
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
});

export const AxiosPrivate = axios.create({
    baseURL: backendUrl,
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
});

// --- Helper Functions (Unchanged) ---
function ensureArray(data) {
    return Array.isArray(data) ? data : [];
}

export const VerifyLoggedIn = () => AxiosPublic.post('accounts/token/refresh/');

AxiosPrivate.interceptors.response.use(
    (initialResponse) => initialResponse,
    async (error) => {
        if (error.response?.status === 401) {
            try {
                const response = await VerifyLoggedIn();
                if (response.status === 200) {
                    return axios.request(error.config);
                }
            } catch (err) {
            }
        }
        return Promise.reject(error);
    }
);

class ApiServiceClass {

    constructor() {}

    _getCurrentLocationSlug() {
        const storedLocation = localStorage.getItem('location');
        if (!storedLocation) {
            // This warning is helpful for debugging if the location is unexpectedly missing.
            console.warn("ApiService: Location not set in localStorage, some API calls might fail.");
            return null;
        }
        return storedLocation.toLowerCase().replace(/ /g, '_');
    }
    get(url) {
        if(development){
            return AxiosPrivate.get(url).then(res => res);
        }else{
            return AxiosPrivate.get(url.replace(/^http:/, 'https:')).then(res => res);
        }

    }

    getFranchiseUsers() {
        const locationSlug = this._getCurrentLocationSlug();
        if (!locationSlug) {
            return Promise.reject(new Error("Current location is not set."));
        }
        const url = `accounts/users/${locationSlug}/`;
        return AxiosPrivate.get(url).then(res => res.data);
    }

    getFranchiseAvailableDates(location, startDate, endDate, days_to_complete = 1, exclude_booking_id = null) {
        if (!location) {
            return Promise.reject(new Error("Franchise location is required."));
        }
        if (!startDate || !endDate) {
            return Promise.reject(new Error("Both startDate and endDate are required."));
        }

        const excludeParam = (exclude_booking_id !== null && exclude_booking_id !== undefined)
            ? `&exclude_booking_id=${exclude_booking_id}`
            : '';
        const url = `franchise/available-dates/${location}/?start_date=${startDate}&end_date=${endDate}&days_to_complete=${days_to_complete}${excludeParam}`;
        return AxiosPrivate.get(url).then(res => res.data);
    }


    createEmployee(employeeData) {
        const locationSlug = this._getCurrentLocationSlug();
        if (!locationSlug) {
            return Promise.reject(new Error("Current location is not set."));
        }
        const url = `accounts/users/${locationSlug}/create/`;
        return AxiosPrivate.post(url, employeeData).then(res => res.data);
    }


    deleteEmployee(userId) {
        const url = `accounts/users/delete/${userId}/`;
        return AxiosPrivate.delete(url);
    }


    createCallbackReminder(leadId, dueDate, description) {
        const url = `actions/create-call-back-reminder/`;

        const payload = {
            lead_id: leadId,
            due_date: dueDate,
            description: description, // Add the description to the payload
        };

        return AxiosPrivate.post(url, payload).then(res => res.data);
    }

    updateAction(actionId, payload) {
        const url = `actions/${actionId}/update/`;
        return AxiosPrivate.patch(url, payload).then(res => res.data);
    }

    getActions(params) {
        const locationSlug = this._getCurrentLocationSlug();
        if (!locationSlug) {
            return Promise.reject(new Error("Current location is not set."));
        }
        const url = `actions/${locationSlug}/all/`;
        const {
            start_date,
            end_date,
            due_date,
            created_by,
            show_completed = false,
            show_past = false,
            page,
        } = params || {};

        const queryParams = {
            start_date,
            end_date,
            due_date,
            show_completed,
            show_past,
        };

        if (created_by) {
            queryParams.created_by = created_by;
        }
        if (page) {
            queryParams.page = page;
        }

        return AxiosPrivate.get(url, {
            params: queryParams
        }).then(res => res);
    }

    setLocation(locationName) {
        if (!locationName) {
            localStorage.removeItem('location');
            return;
        }
        localStorage.setItem('location', locationName);
    }

    getAllSMS(params = {}) {
        const locationSlug = this._getCurrentLocationSlug();
        return AxiosPrivate.get('marketing/all-sms/', { params: { ...params, location: locationSlug } });
    }

    getAllSMSReplies(params = {}) {
        const locationSlug = this._getCurrentLocationSlug();
        return AxiosPrivate.get('marketing/all-sms-responses/', { params: { ...params, location: locationSlug } });
    }

    getAllTemplatedTexts(customer_id) {
        return AxiosPrivate.get(`marketing/all-templated-texts/${customer_id}`);
    }

    getAllFranchisePostcodes() {
        return AxiosPrivate.get(`franchise/franchise-postcodes/${this._getCurrentLocationSlug()}/`);
    }

    lookupFranchisePostcode(outwardPostcode) {
        if (!outwardPostcode) {
            return Promise.reject(new Error("Outward postcode is required."));
        }
        return AxiosPrivate.get(`franchise/lookup-postcode/${encodeURIComponent(outwardPostcode.toUpperCase().trim())}/`).then(res => res.data);
    }

    getFranchiseByName() {
        return AxiosPrivate.get(`franchise/location/${this._getCurrentLocationSlug()}/`).then(res => res.data);
    }

    getFranchiseNotifications() {
        return AxiosPrivate.get(`franchise/notifications/${this._getCurrentLocationSlug()}/`).then(res => res.data);
    }

    sendRotaEmail(email) {
        return AxiosPrivate.post(`marketing/send-rota/${email}/`, null, { params: { locations: this._getCurrentLocationSlug() } });
    }

    getToggledAds() {
        return AxiosPrivate.get(`franchise/ads/${this._getCurrentLocationSlug()}/`).then(res => res.data);
    }

    setFranchiseAdStatus(adType, status, toggleOffReason = '') {
        const data = { ad_type: adType, status, toggleOffReason };
        return AxiosPrivate.post(`franchise/ads/${this._getCurrentLocationSlug()}/`, data);
    }

    postRecurringStatus(value) {
        return AxiosPrivate.post(`franchise/update-recurring-status/${this._getCurrentLocationSlug()}/`, null, {
            params: { 'recurring_leads_purchase': value }
        }).then(res => res.data);
    }

    updateAutoRecharge(enabled, threshold, amount, paymentMethodId, monthlyCapEnabled, monthlyCapAmount) {
        const body = { enabled };
        if (threshold != null) body.threshold = threshold;
        if (amount != null) body.amount = amount;
        if (paymentMethodId != null) body.payment_method_id = paymentMethodId;
        if (monthlyCapEnabled != null) body.monthly_cap_enabled = monthlyCapEnabled;
        if (monthlyCapAmount != null) body.monthly_cap_amount = monthlyCapAmount;
        return AxiosPrivate.post(
            `franchise/update-recurring-status/${this._getCurrentLocationSlug()}/`,
            body
        ).then(res => res.data);
    }

    postFranchiseSettings(franchise, dates, notifications) {
        const data = { franchise, dates, notifications };
        return AxiosPrivate.post(`franchise/update-settings/${this._getCurrentLocationSlug()}/`, data).then(res => res.data);
    }

    updatePhoneNumber(phone) {
        const data = { phone };
        return AxiosPrivate.post("accounts/update-phone/", data).then(res => res);
    }

    isLastPaymentSuccess() {
        return AxiosPrivate.get(`invoices/stripe-last-payment-success/${this._getCurrentLocationSlug()}`).then(res => res.data);
    }

    getStripeInvoices() {
        return AxiosPrivate.get(`invoices/stripe-invoices/${this._getCurrentLocationSlug()}`).then(res => res.data);
    }

    getStripePayouts() {
        return AxiosPrivate.get(`invoices/stripe-payouts/${this._getCurrentLocationSlug()}`).then(res => res.data);
    }
    getStripePaymentMethods() {
        return AxiosPrivate.get(`invoices/stripe-payment-methods/${this._getCurrentLocationSlug()}`).then(res => res.data);
    }
    removePaymentMethod(paymentMethodId) {
        return AxiosPrivate.post(`invoices/stripe-remove-payment-method/${this._getCurrentLocationSlug()}`, { payment_method_id: paymentMethodId }).then(res => res.data);
    }
    getFranchiseFees() {
        return AxiosPrivate.get(`invoices/franchise-fees/${this._getCurrentLocationSlug()}`).then(res => res.data);
    }
    getFranchiseExpenses(franchiseId = null) {
        const params = franchiseId ? { franchise_id: franchiseId } : {};
        return AxiosPrivate.get(`franchise/expenses/${this._getCurrentLocationSlug()}/`, { params }).then(res => res.data);
    }
    createFranchiseExpense(payload, franchiseId = null) {
        const params = franchiseId ? { franchise_id: franchiseId } : {};
        return AxiosPrivate.post(`franchise/expenses/${this._getCurrentLocationSlug()}/`, payload, { params }).then(res => res.data);
    }
    updateFranchiseExpense(expenseId, payload) {
        return AxiosPrivate.patch(`franchise/expenses/${expenseId}/`, payload).then(res => res.data);
    }
    deleteFranchiseExpense(expenseId) {
        return AxiosPrivate.delete(`franchise/expenses/${expenseId}/`).then(res => res.data);
    }
    getEstimatedFranchiseFees() {
        return AxiosPrivate.get(`invoices/estimated-franchise-fees/`).then(res => res.data);
    }
    payFranchiseFee(feeId, paymentMethodId = null) {
        const payload = paymentMethodId ? { payment_method_id: paymentMethodId } : null;
        return AxiosPrivate.post(`invoices/franchise-fees/${this._getCurrentLocationSlug()}/${feeId}/pay`, payload).then(res => res.data);
    }
    createLeadPaymentIntent(amount, turningLeadsOn = false) {
        return AxiosPrivate.post(
            `invoices/stripe-create-leads-invoice/${this._getCurrentLocationSlug()}/${amount}`,
            null,
            { params: { 'turning_leads_on': turningLeadsOn } }
        ).then(res => res.data);
    }
    createSetupIntent() {
        return AxiosPrivate.post(`invoices/stripe-store-payment-method/${this._getCurrentLocationSlug()}/0`).then(res => res.data);
    }

    getAllCalls(page, search, category) {
        const params = { page, search, location: this._getCurrentLocationSlug() };
        if (category && category !== 'All') {
            params.category = category;
        }
        return AxiosPrivate.get(`marketing/all-calls/`, { params }).then(res => res.data);
    }

    queueCallTranscription(callLogId) {
        return AxiosPrivate.post(`marketing/call-logs/${callLogId}/transcribe/`).then(res => res.data);
    }

    getAllAdBalanceChanges(page) {
        const params = { page };
        return AxiosPrivate.get(`leads/balance-changes/${this._getCurrentLocationSlug()}/`, { params }).then(res => res.data);
    }

    getAdBalanceChangesSummary(startDate, endDate) {
        const params = { start_date: startDate, end_date: endDate };
        return AxiosPrivate.get(`leads/balance-changes/${this._getCurrentLocationSlug()}/summary/`, { params }).then(res => res.data);
    }
    getAdBalanceChangesSummaryForLocation(locationSlug, startDate, endDate) {
        const params = { start_date: startDate, end_date: endDate };
        return AxiosPrivate.get(`leads/balance-changes/${locationSlug}/summary/`, { params }).then(res => res.data);
    }

    getAdsActivity(year, month) {
        const params = { year, month };
        return AxiosPrivate.get(`leads/balance-changes/${this._getCurrentLocationSlug()}/ads-activity/`, { params }).then(res => res.data);
    }

    downloadAdBalanceChangesCSV() {
        return AxiosPrivate.get(`leads/balance-changes/${this._getCurrentLocationSlug()}/csv/`, {
            responseType: 'blob', // Important for handling binary data (the CSV file)
        }).then(res => res.data);
    }

    getAllLeadConversions(page) {
        const params = { page };
        return AxiosPrivate.get(`leads/lead-conversions/${this._getCurrentLocationSlug()}/`, { params }).then(res => res.data);
    }

    getSalesSchedule() {
        return AxiosPrivate.get(`accounts/schedule/`).then(res => ensureArray(res.data));
    }

    updateSalesSchedule(data) {
        return AxiosPrivate.post(`accounts/schedule/`, data).then(res => res.data);
    }

    getSalesStatus() {
        return AxiosPrivate.get(`accounts/sales-status/`).then(res => res.data);
    }
    updateSalesStatus(status) {
        return AxiosPrivate.patch(`accounts/sales-status/`, { sales_status: status }).then(res => res.data);
    }
    updateCallRouting(callRouting) {
        return AxiosPrivate.patch(`accounts/sales-status/`, { call_routing: callRouting }).then(res => res.data);
    }

    getTicketStats() {
        return AxiosPrivate.get('tickets/stats/').then(res => res.data);
    }

    // --- Calls (VOIP) ---
    getTwilioToken() {
        return AxiosPrivate.get('calls/token/').then(res => res.data);
    }

    claimIncomingCall(sid) {
        return AxiosPrivate.post('calls/claim/', { sid }).then(res => res.data);
    }

    getMyLatestCall() {
        return AxiosPrivate.get('calls/my-latest/').then(res => res.data);
    }

    lookupCustomer(phone) {
        return AxiosPrivate.get('calls/lookup-customer/', { params: { phone } }).then(res => res.data);
    }

    // --- Voicemails ---
    getVoicemails(params = {}) {
        return AxiosPrivate.get('calls/voicemails/', { params }).then(res => res.data);
    }

    markVoicemailListened(id) {
        return AxiosPrivate.post(`calls/voicemails/${id}/listen/`).then(res => res.data);
    }

    // --- Bulk SMS Campaign ---
    getBulkSMSPreview(lastXLeads, cooldownDays, newCustomersOnly = false, franchiseId = null) {
        return AxiosPrivate.get('marketing/bulk-sms/preview/', {
            params: {
                last_x_leads: lastXLeads,
                cooldown_days: cooldownDays,
                new_customers_only: newCustomersOnly,
                franchise_id: franchiseId
            }
        }).then(res => res.data);
    }

    sendBulkSMS(message, lastXLeads, cooldownDays, newCustomersOnly = false, franchiseId = null) {
        return AxiosPrivate.post('marketing/bulk-sms/send/', {
            message,
            last_x_leads: lastXLeads,
            cooldown_days: cooldownDays,
            new_customers_only: newCustomersOnly,
            franchise_id: franchiseId
        }).then(res => res.data);
    }

    getBulkSMSCampaigns() {
        return AxiosPrivate.get('marketing/bulk-sms/campaigns/').then(res => res.data);
    }

    sendTestBulkSMS(message, leadId) {
        return AxiosPrivate.post('marketing/bulk-sms/test/', {
            message,
            lead_id: leadId
        }).then(res => res.data);
    }

    // --- Bulk WhatsApp Campaign ---
    getBulkWhatsAppTemplates() {
        return AxiosPrivate.get('marketing/bulk-whatsapp/templates/').then(res => res.data);
    }

    getBulkWhatsAppPreview(templateSid, lastXLeads, cooldownDays, newCustomersOnly = false, franchiseId = null) {
        return AxiosPrivate.get('marketing/bulk-whatsapp/preview/', {
            params: {
                template_sid: templateSid,
                last_x_leads: lastXLeads,
                cooldown_days: cooldownDays,
                new_customers_only: newCustomersOnly,
                franchise_id: franchiseId
            }
        }).then(res => res.data);
    }

    sendBulkWhatsApp(templateSid, templateName, templateBodyPreview, variableMapping, lastXLeads, cooldownDays, newCustomersOnly = false, franchiseId = null) {
        return AxiosPrivate.post('marketing/bulk-whatsapp/send/', {
            template_sid: templateSid,
            template_name: templateName,
            template_body_preview: templateBodyPreview,
            variable_mapping: variableMapping,
            last_x_leads: lastXLeads,
            cooldown_days: cooldownDays,
            new_customers_only: newCustomersOnly,
            franchise_id: franchiseId
        }).then(res => res.data);
    }

    getBulkWhatsAppCampaigns() {
        return AxiosPrivate.get('marketing/bulk-whatsapp/campaigns/').then(res => res.data);
    }

    sendTestBulkWhatsApp(templateSid, templateName, templateBodyPreview, variableMapping, leadId) {
        return AxiosPrivate.post('marketing/bulk-whatsapp/test/', {
            template_sid: templateSid,
            template_name: templateName,
            template_body_preview: templateBodyPreview,
            variable_mapping: variableMapping,
            lead_id: leadId
        }).then(res => res.data);
    }

    optOutCustomer(customerId) {
        return AxiosPrivate.post('marketing/opt-out/', {
            customer_id: customerId
        }).then(res => res.data);
    }

    getRonaldSettings() {
        return AxiosPrivate.get('ronald/settings/').then(res => res.data);
    }

    updateRonaldSettings(payload) {
        return AxiosPrivate.put('ronald/settings/', payload).then(res => res.data);
    }

    listRonaldKnowledge(params = {}) {
        return AxiosPrivate.get('ronald/knowledge/', { params }).then(res => res.data);
    }

    createRonaldKnowledge(payload) {
        return AxiosPrivate.post('ronald/knowledge/', payload).then(res => res.data);
    }

    updateRonaldKnowledge(id, payload) {
        return AxiosPrivate.put(`ronald/knowledge/${id}/`, payload).then(res => res.data);
    }

    deleteRonaldKnowledge(id) {
        return AxiosPrivate.delete(`ronald/knowledge/${id}/`).then(res => res.data);
    }

    getRonaldCustomerStatus(customerId) {
        return AxiosPrivate.get(`ronald/customer/${customerId}/status/`).then(res => res.data);
    }

    listRonaldTasks(params = {}) {
        return AxiosPrivate.get('ronald/tasks/', { params }).then(res => res.data);
    }

    getRonaldTask(id) {
        return AxiosPrivate.get(`ronald/tasks/${id}/`).then(res => res.data);
    }

    cancelRonaldTask(id, reason = '') {
        return AxiosPrivate.post(`ronald/tasks/${id}/cancel/`, { reason }).then(res => res.data);
    }

    acknowledgeRonaldTask(id) {
        return AxiosPrivate.post(`ronald/tasks/${id}/acknowledge/`).then(res => res.data);
    }

    forceExecuteRonaldTask(id) {
        return AxiosPrivate.post(`ronald/tasks/${id}/force-execute/`).then(res => res.data);
    }

    triggerRonaldReply(customerId) {
        return AxiosPrivate.post(`ronald/customer/${customerId}/trigger-reply/`).then(res => res.data);
    }

    toggleRonaldAiForCustomer(customerId, enabled) {
        const payload = typeof enabled === "boolean" ? {enabled} : {};
        return AxiosPrivate.post(`ronald/customer/${customerId}/toggle-ai/`, payload).then(res => res.data);
    }

    simulateRonaldReply(payload) {
        return AxiosPrivate.post('ronald/simulate/', payload).then(res => res.data);
    }

    getFranchiseProspectLeads(params) {
        return AxiosPrivate.get('franchisees/crm/franchise-leads/', {params}).then(res => res.data);
    }

    getFranchiseProspectEmailChains() {
        return AxiosPrivate.get('franchisees/crm/email-chains/').then(res => res.data);
    }

    addAllFranchiseProspectsToEmailChain(slug) {
        return AxiosPrivate.post(`franchisees/crm/email-chains/${slug}/add-all/`).then(res => res.data);
    }

    sendFranchiseProspectTestEmail(slug, payload) {
        return AxiosPrivate.post(`franchisees/crm/email-chains/${slug}/send-test/`, payload).then(res => res.data);
    }

    getFranchiseProspectEmailEvents() {
        return AxiosPrivate.get('franchisees/crm/email-events/').then(res => res.data);
    }
}

const GeneralApiService = new ApiServiceClass();
export default GeneralApiService;

// --- Authentication & User ---
export const LoginUser = (user) => AxiosPrivate.post('accounts/login/', user);
export const LogoutUser = () => AxiosPublic.post('accounts/logout/');
export const GetUser = () => AxiosPrivate.get('accounts/user/');

// --- AI & Chat ---
export const GetChats = (params) => AxiosPrivate.get('marketing/chats/', { params });
export const GetUnifiedChats = (params) => AxiosPrivate.get('marketing/unified-chats/', { params });
export const GetCustomerChannels = (customerId) => AxiosPrivate.get(`marketing/customer/${customerId}/channels/`);
export const GetMessagesForChat = (chatId, page) => AxiosPrivate.get(`marketing/chats/${chatId}/messages/`, { params: { page } });
export const UpdateReadStatus = (customerId) => AxiosPrivate.post('marketing/update-read-status/', { customer_id: customerId });
export const PostSMSTextResponse = (messageProps) => AxiosPrivate.post('marketing/send-sms-responses/', { message_props: messageProps });

// --- WhatsApp ---
export const GetWhatsAppChats = (params) => AxiosPrivate.get('marketing/whatsapp/chats/', { params });
export const GetWhatsAppMessagesForChat = (chatId, page) => AxiosPrivate.get(`marketing/whatsapp/chats/${chatId}/messages/`, { params: { page } });
export const UpdateWhatsAppReadStatus = (customerId) => AxiosPrivate.post('marketing/whatsapp/update-read-status/', { customer_id: customerId });
export const PostWhatsAppResponse = (messageProps) => AxiosPrivate.post('marketing/whatsapp/send-responses/', { message_props: messageProps });

// --- Email ---
export const GetEmailMessagesForChat = (chatId, page) => AxiosPrivate.get(`marketing/email/chats/${chatId}/messages/`, { params: { page } });
export const PostEmailResponse = (data) => AxiosPrivate.post('marketing/email/send-response/', data);
export const UpdateEmailReadStatus = (customerId) => AxiosPrivate.post('marketing/email/update-read-status/', { customer_id: customerId });

// --- Franchise ---
export const GetAllFranchises = () => AxiosPrivate.get('franchise/all/');

// --- Franchise: Vans ---

export const GetFranchiseVans = (franchiseId) => AxiosPrivate.get(`franchise/franchise-vans/`, { params: { franchise_id: franchiseId } });



export const CreateFranchiseVan = (franchiseId, vanName) => AxiosPrivate.post(`franchise/franchise-vans/`, { franchise_id: franchiseId, name: vanName });


export const UpdateFranchiseVan = (vanId, data) => AxiosPrivate.patch(`franchise/franchise-vans/${vanId}/`, data);


export const DeleteFranchiseVan = (vanId) => AxiosPrivate.delete(`franchise/franchise-vans/${vanId}/`);


export const CreateVanBlockedDate = (vanId, blockData) => AxiosPrivate.post(`franchise/franchise-vans/${vanId}/blocked-dates/`, blockData);


export const DeleteVanBlockedDate = (vanId, blockId) => AxiosPrivate.delete(`franchise/franchise-vans/${vanId}/blocked-dates/${blockId}/`);

export const UpdateVanBlockedDate = (vanId, blockId, data) => AxiosPrivate.patch(`franchise/franchise-vans/${vanId}/blocked-dates/${blockId}/`, data);

export const CreateVanDayOverride = (vanId, overrideData) => AxiosPrivate.post(`franchise/franchise-vans/${vanId}/day-overrides/`, overrideData);

export const DeleteVanDayOverride = (vanId, overrideId) => AxiosPrivate.delete(`franchise/franchise-vans/${vanId}/day-overrides/${overrideId}/`);


// --- Stripe/Invoices ---
export const CreateStripeAccountForFranchise = (franchise) => AxiosPrivate.post(`invoices/stripe-create-account/${franchise}`).then(res => res.data);
export const CreateStripePaymentMethod = (franchise, amount) => AxiosPrivate.post(`invoices/stripe-store-payment-method/${franchise}/${amount}`).then(res => res.data);
export const PurchaseLeads = (franchise, cost, turningLeadsOn) => AxiosPrivate.post(`invoices/stripe-create-leads-invoice/${franchise}/${cost}`, null, { params: { 'turning_leads_on': turningLeadsOn } }).then(res => res.data);

// --- Knowledge Base: Documents & Courses ---
export const GetAllDocuments = () => AxiosPrivate.get('documents/all/').then(res => res.data);
export const GetDocumentBySlug = (slug) => AxiosPrivate.get(`documents/${slug}/`).then(res => res.data);
export const GetDocumentByCourseSlug = (slug) => AxiosPrivate.get(`documents/course/documents/${slug}/`).then(res => res.data);
export const GetAllCourses = () => AxiosPrivate.get('documents/course/all/').then(res => res.data);
export const GetCourseBySlug = (slug) => AxiosPrivate.get(`documents/course/${slug}/`).then(res => res.data);
export const GetCourseProgress = (slug) => AxiosPrivate.get(`documents/course/${slug}/progress/`).then(res => res.data);

// --- Knowledge Base: Quiz System ---
export const SubmitQuiz = (data) => AxiosPrivate.post('documents/quiz/submit/', data).then(res => res.data);
export const GetQuizAttempts = (quizId) => AxiosPrivate.get(`documents/quiz/${quizId}/attempts/`).then(res => res.data);
export const GetQuizAttemptDetail = (attemptId) => AxiosPrivate.get(`documents/quiz/attempt/${attemptId}/`).then(res => res.data);

// --- Knowledge Base: Document Progress ---
export const ToggleDocumentComplete = (slug) => AxiosPrivate.post(`documents/document/${slug}/toggle-complete/`).then(res => res.data);

// --- Support Tickets ---
export const GetTickets = (params) => AxiosPrivate.get('tickets/', { params });
export const CreateTicket = (data) => AxiosPrivate.post('tickets/', data, { headers: { 'Content-Type': 'multipart/form-data' } });
export const GetTicketDetail = (id) => AxiosPrivate.get(`tickets/${id}/`);
export const UpdateTicket = (id, data) => AxiosPrivate.patch(`tickets/${id}/`, data);
export const GetTicketMessages = (id, page) => AxiosPrivate.get(`tickets/${id}/messages/`, { params: { page } });
export const PostTicketMessage = (id, data) => AxiosPrivate.post(`tickets/${id}/messages/`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
export const CloseTicket = (id) => AxiosPrivate.post(`tickets/${id}/close/`);
export const ReopenTicket = (id) => AxiosPrivate.post(`tickets/${id}/reopen/`);
export const GetTicketStats = () => AxiosPrivate.get('tickets/stats/');
export const GetSuperAdminsList = () => AxiosPrivate.get('tickets/super-admins/');

// --- Other ---
export const GetAddressFromPostcode = (postcode) => axios.get(`https://api.ideal-postcodes.co.uk/v1/autocomplete/addresses?api_key=ak_mo5xwj8i0NBOVC6M3XcGUdmY9ghpb&q=${encodeURIComponent(postcode)}`).then(res => {
    // Transform Ideal Postcodes response to match existing format
    const suggestions = res.data?.result?.hits?.map(s => {
        // Remove the outward postcode from end of address (e.g., ", OL12" -> "")
        const address = s.suggestion?.replace(/,\s*[A-Z]{1,2}\d{1,2}[A-Z]?\s*$/, '') || s.suggestion;
        return {
            address,
            id: s.udprn?.toString() || s.id
        };
    }) || [];
    return { suggestions };
});
